import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Input,
  Button,
  Form,
  FormGroup,
  Label,
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody
} from "reactstrap";
import axios from "axios";
import Breadcrumbs from "components/Common/Breadcrumb";
import { Formik, useFormik, ErrorMessage, Field, FieldArray } from "formik";
import { FiXCircle } from "react-icons/fi";
import CustomSelect from "components/Common/custom-select";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import CustomMonthPicker from "common/CustomMonthPicker";
import { fetchActiveEmployees, fetchPayrollAllowance, fetchPayrollAllowancesDropdown, removePayrollAllowance, savePayrollAllowance, getMonthlyAllowanceTemplate, allowancelistImport } from "store/actions";
import { FiTrash2 } from "react-icons/fi";
import Swal from "sweetalert2";

const Allowance = () => {
  const formikRef = useRef();
  const [formData, setFormData] = useState({
    month: "Jan-2025",
    allowance: "OT",
    empCode: "",
    name: "",
    amount: "",
    remarks: "",
  });

  const [salaryAllowanceDefault, setSalaryAllowanceDefault] = useState({ emp_id: "", emp_no: "", emp_name: "", amount: "", pay_allow_id: "0", remarks: "", enable_status: false, entry_type: 2 });
  const [salaryAllowance, setSalaryAllowance] = useState([]);
  const [deleteData, setDeleteData] = useState([]);

  const [searchStatus, setSearchStatus] = useState(false);
  const [saveStatus, setSaveStatus] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    allowanceOptions,
    payrollAllowances,
    employees,
    importedData,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    allowanceOptions: state.PayrollSalaryAllowanceReducer?.allowanceOptions ?? [],
    payrollAllowances: state.PayrollSalaryAllowanceReducer?.payrollAllowances ?? [],
    employees: state.EmployeeDetailsReducer?.employeesActiveList ?? [],
    importedData: state.PayrollSalaryAllowanceReducer?.importedData ?? [],
    loading: state.PayrollSalaryAllowanceReducer?.loading,
    error: state.PayrollSalaryAllowanceReducer?.error,
    success: state.PayrollSalaryAllowanceReducer?.success,
  }));
  const [employeeData, setEmployeeData] = useState([]);
  const [allowanceData, setAllowanceData] = useState([]);

  useEffect(() => {
    setEmployeeData(employees)
  }, [employees]);

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchPayrollAllowancesDropdown(unitId, 2))
    dispatch(fetchActiveEmployees(unitId))
    setSalaryAllowance([salaryAllowanceDefault]);
  }, [dispatch]);

  useEffect(() => {
    if (payrollAllowances && payrollAllowances.length > 0) {
      resetEmployeeData(payrollAllowances);
      setSalaryAllowance([...payrollAllowances, salaryAllowanceDefault]);
    } else {
      setSalaryAllowance([salaryAllowanceDefault]);
    }
  }, [payrollAllowances])

  useEffect(() => {
    if (success) {
      const formData = new FormData();
      formData.append("sal_month", formik.values?.month);
      formData.append("pay_allow_id", formik.values?.allowance?.value);
      formData.append("unit_id", authDetails?.unit_id);
      formData.append("create_by", authDetails?.user_id);
      dispatch(fetchPayrollAllowance(formData));
    }
  }, [authDetails, success, dispatch]);

  const initialDCancelRemarksValues = {
    remarks: ""
  };

  const validationDCancelRemarksSchema = Yup.object({
    remarks: Yup.string().required("Remarks is required"),
  });

  const initialValues = {
    allowance: ""
  };

  const validationSchema = Yup.object({
    allowance: Yup.object().nullable().required("Allowance is required"),
  });

  const deductionSchema = Yup.object().shape({
    emp_id: '',
    emp_code: '',
    emp_no: Yup.object().test('emp_no', 'Employee Number is required', function (value) {
      const { path, parent, options } = this;
      const index = parseInt(path.match(/\d+/)?.[0] ?? -1);
      //const lastIndex = options.context?.length - 1;
      const lastIndex = salaryAllowance?.length - 1;

      const isLastRow = index === lastIndex;
      const hasAnyValue = !!parent.emp_no || !!parent.emp_name || !!parent.amount || !!parent.remarks;

      if (isLastRow && !hasAnyValue) {
        return true; // ✅ skip validation if empty
      }

      return !!value; // ✅ validate if value is expected
    }).nullable(),

    emp_name: Yup.string().test('emp_name', 'Name is required', function (value) {
      const { path, parent, options } = this;
      const index = parseInt(path.match(/\d+/)?.[0] ?? -1);
      //const lastIndex = options.context?.length - 1;
      const lastIndex = salaryAllowance?.length - 1;

      const isLastRow = index === lastIndex;
      const hasAnyValue = !!parent.emp_no || !!parent.emp_name || !!parent.amount || !!parent.remarks;

      if (isLastRow && !hasAnyValue) {
        return true; // ✅ skip validation if empty
      }

      return !!value; // ✅ validate if value is expected
    }),

    amount: Yup.number().test('amount', 'Amount is required', function (value) {
      const { path, parent, options } = this;
      const index = parseInt(path.match(/\d+/)?.[0] ?? -1);
      //const lastIndex = options.context?.length - 1;
      const lastIndex = salaryAllowance?.length - 1;

      const isLastRow = index === lastIndex;
      const hasAnyValue = !!parent.emp_no || !!parent.emp_name || !!parent.amount || !!parent.remarks;

      if (isLastRow && !hasAnyValue) {
        return true; // ✅ skip validation if empty
      }

      return value !== undefined && value !== ''; // ✅ validate if filled
    }),

    remarks: Yup.string().test('remarks', 'Remarks is required', function (value) {
      const { path, parent, options } = this;
      const index = parseInt(path.match(/\d+/)?.[0] ?? -1);
      //const lastIndex = options.context?.length - 1;
      const lastIndex = salaryAllowance?.length - 1;

      const isLastRow = index === lastIndex;
      const hasAnyValue = !!parent.emp_no || !!parent.emp_name || !!parent.amount || !!parent.remarks;

      if (isLastRow && !hasAnyValue) {
        return true; // ✅ skip validation if empty
      }

      return !!value; // ✅ validate if value is expected
    }),
  });

  const validationDeductionSchema = Yup.object().shape({
    allowance: Yup.array().of(deductionSchema)
  });

  const formik = useFormik({
    initialValues: {
      month: "",
      allowance: "",
    },
    validationSchema: Yup.object({
      allowance: Yup.object().required("Allowance is required"),
      month: Yup.date()
        .required("Month is required")
        .typeError("Month must be a valid date"),
    }),
    onSubmit: (values) => {
      setSalaryAllowance([]);
      const formData = new FormData();
      formData.append("sal_month", values?.month);
      formData.append("pay_allow_id", values?.allowance?.value);
      formData.append("unit_id", authDetails?.unit_id);
      formData.append("create_by", authDetails?.user_id);
      dispatch(fetchPayrollAllowance(formData));
      setSearchStatus(true);
    },
  });

  const resetEmployeeData = (fixedDeduction) => {
    const withoutSelectedEmployees = employees.filter(function (item) {
      return !fixedDeduction.some(function (fd) {
        return fd.emp_id == item.value;
      });
    });
    setEmployeeData(withoutSelectedEmployees);
  };

  const handleNextRow = (index) => {
    if (formikRef.current) {
      formikRef.current[index]?.focus();

      formikRef.current.validateForm().then((errors) => {
        formikRef.current.handleSubmit();
        if (!errors.allowance) {
          const findEmptyDeduction = formikRef.current.values.allowance.filter(emp => emp.emp_no === '' || emp.emp_name === '' || emp.amount === '' || emp.remarks === '');
          if (findEmptyDeduction.length <= 0) {
            let fixedDedData = [...formikRef.current.values.allowance, ...[salaryAllowanceDefault]];
            fixedDedData.forEach(function (element) {
              if (element?.emp_no) {
                // element.enable_status = true;
                // element.emp_no = { value: element.emp_id, label: element.emp_no + ' - ' + element.emp_name };
                element.pay_allow_id = 0;
              }
            });
            setSalaryAllowance(fixedDedData);
            //Remove the selected employees from the list
            resetEmployeeData(fixedDedData);
          }
        }
      });

    }
  }

  const handleSave = () => {
    //formik.handleSubmit();
    if (formikRef.current) {
      formikRef.current.validateForm().then((errors) => {
        formikRef.current.handleSubmit();
        if (!errors.allowance) {
          let submitData = formikRef.current.values.allowance;
          submitData = submitData.filter(function (obj) {
            return obj.emp_name !== '';
          });

          const newFixedDeductionCount = submitData.filter(entry => entry.entry_type === 2).length;
          if (newFixedDeductionCount > 0) {
            const keysToRemove = ['emp_name', 'emp_no', 'enable_status', 'entry_type'];

            submitData = submitData.map(obj => {
              const newObj = { ...obj };
              keysToRemove.forEach(key => delete newObj[key]);
              return newObj;
            });

            let lst_employee_allowance_data = { allow: submitData };
            const formData = new FormData();
            formData.append("lst_employee_allowance", JSON.stringify(lst_employee_allowance_data));
            formData.append("allow_type_id", formik.values.allowance?.value);
            formData.append("salary_month", formik.values.month);
            formData.append("unit_id", authDetails?.unit_id);
            formData.append("create_by", authDetails?.user_id);
            dispatch(savePayrollAllowance(formData));
          }
        }
      })
    }
  };

  const removeByIndex = (array, indexToRemove) => {
    return array.filter((_, index) => index !== indexToRemove);
  };

  const handleDelete = (ded, index) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "No",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6"
    }).then((result) => {
      if (result.isConfirmed) {
        if (ded?.entry_type === 2) {
          const newFixedDedData = removeByIndex(salaryAllowance, index);
          setSalaryAllowance(newFixedDedData);
          resetEmployeeData(newFixedDedData);
        } else {
          setDeleteData(ded);
          setIsOpen(true);
        }
      }
    });
  };

  const cancelRemarksFormik = useFormik({
    initialValues: {
      remarks: "",
    },
    validationSchema: Yup.object({
      remarks: Yup.string().required("Remarks are required"),
    }),
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("allow_type_id", deleteData?.pay_allow_id);
      formData.append("remarks", values?.remarks);
      formData.append("create_by", authDetails?.user_id);
      dispatch(removePayrollAllowance(formData));
      setDeleteData();
      setIsOpen(false);
    },
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const handleGetTemplate = () => {
    // Prepare FormData
    const formData = new FormData();
    formData.append("salary_month", formik.values.month); // pass month here
    formData.append("payAlwn_id", formik.values.allowance?.value); // pass month here
    formData.append("unit_id", authDetails?.unit_id);
    formData.append("create_by", authDetails?.user_id);

    // Dispatch Redux action
    dispatch(getMonthlyAllowanceTemplate(formData));
  };

  const fileInputRef = useRef(null);
  const handleFileChange = (e) => { setSelectedFile(e.target.files[0]); };
  const handleImport = () => {
    if (!selectedFile) { alert("Please select a file first!"); return; }
    const formData = new FormData();
    formData.append("excelFile", selectedFile);
    formData.append("payAlwn_id", formik.values.allowance?.value);
    formData.append("salary_month", formik.values.month);
    formData.append("create_by", authDetails?.user_id);
    formData.append("unit_id", authDetails?.unit_id);
    dispatch(allowancelistImport(formData));
  };

  useEffect(() => {
    if (success && importedData?.length > 0) {
      const current = formikRef.current.values.allowance || [];
      const formattedData = importedData.map(item => {
        const obj = { ...item, emp_no: { value: item.emp_id, label: `${item.emp_no} - ${item.emp_name}` } };
        return obj;
      });

      const findEmptyDeduction = formikRef.current.values.allowance.filter(emp => emp.emp_no === '' || emp.emp_name === '' || emp.amount === '' || emp.remarks === '');
      const filtered = current.filter(row => row.emp_code && row.emp_code !== "");
      formikRef.current?.setFieldValue("allowance", [...filtered, ...formattedData, ...findEmptyDeduction]);
      resetEmployeeData(formikRef.current.values.allowance);
    }
  }, [success, importedData]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Payroll / Salary" breadcrumbItem="Allowance" />

          <Card>
            <CardBody>
              <Form onSubmit={formik.handleSubmit}>
                <Row className="mt-4">
                  <Col md={3}>
                    <FormGroup>
                      <Label for="month">
                        Month <span className="text-danger">*</span>
                      </Label>
                      <CustomMonthPicker
                        name="month"
                        value={formik.values.month}
                        onChange={(date) => formik.setFieldValue("month", date)}
                        onBlur={formik.handleBlur}
                        className={`form-control ${formik.touched.month && formik.errors.month ? "is-invalid" : ""}`}
                      />
                      {formik.touched.month && formik.errors.month && (
                        <div className="text-danger">{formik.errors.month}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label for="allowance">Allowance <span className="text-danger">*</span></Label>
                      <CustomSelect
                        id="allowance"
                        name="allowance"
                        options={(allowanceOptions || []).map(item => ({ value: item.pay_allow_id, label: item.short_name }))}
                        value={allowanceData}
                        // onChange={selectedOption => formik.setFieldValue("allowance", selectedOption)}
                        onChange={(selectedOption) => { formik.setFieldValue("allowance", selectedOption); setSalaryAllowance([]); setAllowanceData(selectedOption); setSearchStatus(false); }}
                        onBlur={() => formik.setFieldTouched("allowance", true)}  // ✅ Ensure onBlur is handled
                        classNamePrefix="react-select"
                      />
                      {formik.touched.allowance && formik.errors.allowance && (
                        <div className="text-danger">{formik.errors.allowance}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="auto" className="d-flex align-items-end mb-4">
                    <Button type="submit" className="btn button-search me-2">
                      <i className="fas fa-search me-1" /> Search
                    </Button>
                    <Button type="button" className="btn button-save me-2" onClick={handleSave}>
                      <i className="fas fa-save me-1" /> Save
                    </Button>
                    <Button
                      type="button"
                      className="btn button-cancel me-2"
                      onClick={() => {
                        Swal.fire({
                          title: "Are you sure?",
                          text: "All unsaved data will be cleared.",
                          icon: "warning",
                          showCancelButton: true,
                          confirmButtonColor: "#3085d6",
                          cancelButtonColor: "#d33",
                          confirmButtonText: "Yes, Cancel",
                          cancelButtonText: "No"
                        }).then((result) => {
                          if (result.isConfirmed) {
                            formik.resetForm();
                            setAllowanceData(null);
                            setSalaryAllowance([salaryAllowanceDefault]);
                            setEmployeeData(employees);
                            setSearchStatus(false);
                            setSelectedFile(null);
                            setSelectedFile(null);
                            if (fileInputRef.current) { fileInputRef.current.value = ""; }
                            formik.setFieldValue("file", "");
                          }
                        });
                      }}
                    >
                      <i className="fas fa-sync me-1" /> Cancel
                    </Button>
                    <Button type="button" color="primary" className="button-export" onClick={handleGetTemplate}>
                      <i className="fas fa-download me-1" /> Get Template
                    </Button>
                  </Col>
                </Row>
                <Row className="align-items-end">
                  <Col md={3}>
                    <Label for="file">File <span className="text-danger">*</span></Label>
                    <Input
                      id="file"
                      name="file"
                      type="file"
                      innerRef={fileInputRef}
                      onChange={handleFileChange}
                      accept=".xls,.xlsx"
                      onBlur={formik.handleBlur}
                      className={`form-control ${formik.touched.file && formik.errors.file ? "is-invalid" : ""
                        }`}
                    />
                    {formik.touched.file && formik.errors.file && (
                      <div className="invalid-feedback d-block">{formik.errors.file}</div>
                    )}
                  </Col>
                  <Col md={2}>
                    <Button type="button" color="success" className="button-export" onClick={handleImport} disabled={!selectedFile}>
                      <i className="fas fa-file-import me-1" /> Import
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
          {/* <Card>
            <CardBody>
              <Form onSubmit={handleSubmit}>
                <Table bordered className="mt-4">
                  <thead>
                    <tr>
                      <th>Del</th>
                      <th>Emp Code</th>
                      <th>Name</th>
                      <th>Amount</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <Button size="sm" className="btn button-delete"><i className="mdi mdi-delete" /></Button>
                      </td>
                      <td>
                        <Input type="select" name="empCode" value={formData.empCode} onChange={handleChange}>
                          <option value="">Select</option>
                          {employees.map((emp) => (
                            <option key={emp.code} value={emp.code}>
                              {emp.code}-{emp.name}
                            </option>
                          ))}
                        </Input>
                      </td>
                      <td>
                        <Input type="text" name="name" value={formData.name} disabled />
                      </td>
                      <td>
                        <Input type="number" name="amount" value={formData.amount} onChange={handleChange} />
                      </td>
                      <td>
                        <Input type="text" name="remarks" value={formData.remarks} onChange={handleChange} />
                      </td>
                    </tr>
                  </tbody>
                </Table>
                <Col className="text-end">
                  <Button type="button" className="btn button-save">
                    <i className="fas fa-save me-1" /> Save
                  </Button>
                </Col>
              </Form>
              {records.length > 0 && (
                <Table bordered className="mt-4">
                  <thead>
                    <tr>
                      <th>Emp Code</th>
                      <th>Name</th>
                      <th>Amount</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((rec, index) => (
                      <tr key={index}>
                        <td>{rec.empCode}</td>
                        <td>{rec.name}</td>
                        <td>{rec.amount}</td>
                        <td>{rec.remarks}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card> */}

          <Card>
            <CardBody>
              <Formik
                initialValues={{ allowance: salaryAllowance }}
                enableReinitialize
                innerRef={formikRef}
                validationSchema={validationDeductionSchema}
                //context={{ length: fixedDeductionData?.length ? fixedDeductionData?.length:0 }} // Required for row logic
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={(values) => {
                  //console.log('Submitted',values);
                }}>
                {({ values, setFieldValue, setFieldTouched, validateField, errors, touched }) => (
                  <Form>
                    <div className="table-responsive overflow-visible">
                      <Table bordered>
                        <thead className="thead-dark">
                          <tr>
                            <th className="text-center">Action</th>
                            <th>Emp Code<span className="text-danger ms-1">*</span></th>
                            <th>Name</th>
                            <th>Amount<span className="text-danger ms-1">*</span></th>
                            <th>Remarks<span className="text-danger ms-1">*</span></th>
                          </tr>
                        </thead>
                        <tbody>
                          <FieldArray name="allowance">
                            {() => (
                              typeof allowanceData === 'object' && allowanceData !== null && values?.allowance?.length > 0 ? (
                                values.allowance.map((ded, index) => (
                                  <tr key={index}>
                                    <td className="text-center">
                                      {(salaryAllowance?.length > 1 && values?.allowance?.length - 1 != index) && (
                                        // <FiXCircle size={20} color="red" style={{ cursor: "pointer" }} onClick={() => handleDelete(ded, index)} />)} 
                                        <FiTrash2 size={20} color="red" style={{ cursor: "pointer" }} onClick={() => handleDelete(ded, index)} />)}                                    </td>
                                    <td>
                                      {
                                        ded.enable_status ?
                                          <>
                                            <Input name={`allowance[${index}].emp_code`} disabled value={ded.emp_code} title={`${ded.emp_code} - ${ded.emp_name}`} />
                                            <Input type="hidden" name={`allowance[${index}].emp_no`} disabled value={ded.emp_no} />
                                          </>
                                          :
                                          <>
                                            <div title={ded.emp_no?.label || ""}>
                                              <CustomSelect
                                                options={employeeData}
                                                // isDisabled={ded.enable_status}
                                                disabled={ded.entry_type !== 2}
                                                name={`allowance[${index}].emp_no`}
                                                // value={employeeData.find((option) => option.value == ded.emp_id)} // Store full object
                                                value={ded.emp_no || null}
                                                onChange={(selectedOption) => {
                                                  if (!selectedOption) {
                                                    setFieldValue(`allowance[${index}].emp_no`, null);
                                                    setFieldValue(`allowance[${index}].emp_id`, "");
                                                    setFieldValue(`allowance[${index}].emp_code`, "");
                                                    setFieldValue(`allowance[${index}].emp_name`, "");
                                                    return;
                                                  }
                                                  const [emp_code, emp_name] = selectedOption.label.split(" - ");

                                                  setFieldValue(`allowance[${index}].emp_no`, selectedOption);
                                                  setFieldTouched(`allowance[${index}].emp_no`, true, true);
                                                  validateField(`allowance[${index}].emp_no`);

                                                  setFieldValue(`allowance[${index}].emp_id`, selectedOption.value);
                                                  setFieldTouched(`allowance[${index}].emp_id`, true, true);

                                                  setFieldValue(`allowance[${index}].emp_code`, emp_code);
                                                  setFieldTouched(`allowance[${index}].emp_code`, true, true);

                                                  setFieldValue(`allowance[${index}].emp_name`, emp_name);
                                                  setFieldTouched(`allowance[${index}].emp_name`, true, true);
                                                  validateField(`allowance[${index}].emp_name`);
                                                }
                                                }

                                                onBlur={() => { setFieldTouched(`allowance[${index}].emp_name`, true); setFieldTouched(`allowance[${index}].emp_no`, true); handleNextRow(index); }}
                                              />
                                            </div>
                                            <ErrorMessage name={`allowance[${index}].emp_no`} component="div" className="error" />
                                          </>
                                      }
                                    </td>
                                    <td>
                                      <Input name={`allowance[${index}].emp_name`} disabled value={ded.emp_name} title={ded.emp_name} onChange={(e) => { handleNextRow(index); }} onBlur={(e) => { handleNextRow(index); }} />
                                      <ErrorMessage name={`allowance[${index}].emp_name`} component="div" className="error" />
                                    </td>
                                    <td>
                                      <Input name={`allowance[${index}].amount`} type="number" maxLength={5} className="text-end" disabled={ded.entry_type !== 2} value={ded.amount}
                                        onKeyDown={(e) => {
                                          if (["-", "+", "*", "/", "e", "E", "."].includes(e.key)) {
                                            e.preventDefault();
                                          }
                                        }}
                                        onInput={(e) => {
                                          const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 5);
                                          setFieldValue(`allowance[${index}].amount`, value);
                                        }}
                                        onBlur={(e) => { handleNextRow(index); }}
                                      />
                                      <ErrorMessage name={`allowance[${index}].amount`} component="div" className="error" />
                                    </td>
                                    <td>
                                      <Input name={`allowance[${index}].remarks`} maxLength={100} value={ded.remarks} disabled={ded.entry_type !== 2} title={ded.remarks}
                                        onInput={(e) => {
                                          const value = e.target.value.slice(0, 100);
                                          setFieldValue(`allowance[${index}].remarks`, value);
                                          //handleNextRow(index);                                    
                                        }
                                        }
                                        onBlur={(e) => { handleNextRow(index); }}
                                      />
                                      <ErrorMessage name={`allowance[${index}].remarks`} component="div" className="error" />
                                    </td>
                                  </tr>
                                ))
                              ) : '')
                            }
                          </FieldArray>
                        </tbody>
                      </Table>
                    </div>
                  </Form>
                )}
              </Formik>
            </CardBody>
          </Card>
          <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
            <ModalHeader tag="h4">
              Remove Allowance <span className="text-danger">*</span>
            </ModalHeader>
            <ModalBody>
              <form onSubmit={cancelRemarksFormik.handleSubmit}>
                <Row form>
                  <Col xs={12}>
                    <div className="mb-3">
                      <Label className="form-label">Remarks <span className="text-danger">*</span></Label>
                      <textarea
                        name="remarks"
                        className={`form-control ${cancelRemarksFormik.touched.remarks && cancelRemarksFormik.errors.remarks ? "is-invalid" : ""}`}
                        value={cancelRemarksFormik.values.remarks}
                        onChange={cancelRemarksFormik.handleChange}
                        onBlur={cancelRemarksFormik.handleBlur}
                      />
                      {cancelRemarksFormik.touched.remarks && cancelRemarksFormik.errors.remarks && (
                        <div className="text-danger">{cancelRemarksFormik.errors.remarks}</div>
                      )}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div className="text-end">
                      <button type="submit" className="me-2 btn btn-primary">Save</button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          Swal.fire({
                            title: "Are you sure?",
                            text: "Do you want to close this popup?",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonText: "Yes, Close",
                            cancelButtonText: "No",
                            confirmButtonColor: "#3085d6",
                            cancelButtonColor: "#d33"
                          }).then((result) => {
                            if (result.isConfirmed) {
                              setIsOpen(false);
                              setDeleteData();
                            }
                          });
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </Col>
                </Row>
              </form>
            </ModalBody>
          </Modal>

        </Container>
      </div>
    </React.Fragment>
  );
};

export default Allowance;
